import React from 'react';
import { createPortal } from 'react-dom';
import { useIsPresent } from 'framer-motion';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  spacing,
  typography,
  zIndex,
} from '../../style-utils/theme';
import { spin } from '../../style-utils/motion';
import { LogoMark } from '../logo/logo-mark';
import { m } from '../../motion/styled';
import { loader } from '../../motion/presets';

/**
 * Full-screen branded loading state — the Lobbero mark + wordmark over a
 * self-contained animated ring spinner. Deliberately does NOT reuse the legacy
 * icons/Spinner (it rendered invisibly here): everything below is inline so the
 * loader always shows, regardless of ancestor transforms or theme drift.
 *
 * IT FADES. Mount it inside a `<Presence>` with a key and it fades in as it
 * arrives and — the part that matters — fades OUT over the screen that has
 * just been built beneath it, so the first thing a person sees after signing
 * in emerges from the loader instead of replacing it in one frame.
 */

const Overlay = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.overlay};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md};
  background: ${palette.porcelain};
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const Wordmark = styled.span`
  font-family: ${typography.display};
  font-weight: ${typography.weight.bold};
  font-size: ${typography.size.xxl}px;
  letter-spacing: ${typography.tracking.tight};
  color: ${palette.ink};
  line-height: 1;
`;

/* Self-contained ring spinner — a rotating conic border. Always visible. */
const Ring = styled.span`
  width: ${spacing.xl};
  height: ${spacing.xl};
  border-radius: ${radius.circle};
  border: ${spacing.half} solid ${palette.lineStrong};
  border-top-color: ${palette.lagoon};
  animation: ${spin} 800ms linear infinite;
`;

const Caption = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.slate};
`;

interface BrandedLoaderProps {
  label?: string;
}

/* Portaled to <body>: ancestors in the wizard pane animate with transform /
 * will-change, which turns position:fixed into "fixed inside the pane" —
 * escaping them is the only way this reliably centers on the screen. */
export const BrandedLoader: React.FC<BrandedLoaderProps> = ({ label }) => {
  /* THE MOMENT IT STARTS LEAVING, IT STOPS CATCHING CLICKS. It fades out over
     the screen built beneath it, and while it faded it still took every click:
     a "Sign in" pressed in that moment hit the fading overlay and was lost
     with no request sent (analytics suite, 2026-09-23 — reproduced with the
     loader on top of the button at 36% opacity). Done here rather than in the
     `loader` variants so the animation itself is untouched. */
  const isPresent = useIsPresent();

  return createPortal(
    <Overlay
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-branded-loader
      style={isPresent ? undefined : { pointerEvents: 'none' }}
      variants={loader}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <Brand>
        <LogoMark size={44} aria-hidden="true" />
        <Wordmark>Lobbero</Wordmark>
      </Brand>
      <Ring aria-hidden="true" />
      {label ? <Caption>{label}</Caption> : null}
    </Overlay>,
    document.body
  );
};
